import { getContrastColor } from '@/lib/colorUtils';
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, TrendingUp, TrendingDown, Minus, Home, Clock, Tag, Sparkles } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { RichTextEditor } from "@/components/RichTextEditor";
import { logger } from "@/lib/logger";

interface NeighborhoodDataProps {
  northId: string;
  northData: any;
  isPublished?: boolean;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  onUpdate?: () => void; // Callback to refresh parent component data
}

interface NeighborhoodMetrics {
  location: string;
  metrics: {
    newListings: number;
    pendingListings: number;
    priceReductions: number;
    priceReductionRate: number;
    activeListings: number;
    soldListings: number;
    activeDomMedian: number | null;
    soldDomMedian: number | null;
  };
  analysis: {
    demandRating: number;
    buyerTrend: 'Rising' | 'Stable' | 'Easing';
    monthsOfInventory: number | null;
  };
  updated_at: string;
}

export const NeighborhoodData = ({ northId, northData, isPublished, primaryColor, secondaryColor, onUpdate }: NeighborhoodDataProps) => {
  const [data, setData] = useState<NeighborhoodMetrics | null>(null);
  const [showOnReport, setShowOnReport] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [neighborhoodSummary, setNeighborhoodSummary] = useState("");
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    loadExistingData();
  }, [northId]);

  const loadExistingData = async () => {
    try {
      const { data: north, error } = await supabase
        .from('norths')
        .select('neighborhood_data, neighborhood_data_updated_at, show_neighborhood_on_report, neighborhood_summary')
        .eq('id', northId)
        .single();

      if (error) throw error;

      if (north?.neighborhood_data) {
        setData(north.neighborhood_data as unknown as NeighborhoodMetrics);
      }
      if (north?.show_neighborhood_on_report !== null && north?.show_neighborhood_on_report !== undefined) {
        setShowOnReport(north.show_neighborhood_on_report);
      }
      if (north?.neighborhood_summary) {
        setNeighborhoodSummary(north.neighborhood_summary);
      }
    } catch (error) {
      logger.error('Error loading neighborhood data:', error);
    }
  };

  const handleGenerateData = async () => {
    setIsLoading(true);
    try {
      toast({
        title: "Analyzing neighborhood...",
        description: "This may take a moment",
      });

      // Use ZIP code for neighborhood searches to get broader area results (consistent with Create North)
      const { data: result, error } = await supabase.functions.invoke('fetch-neighborhood-data', {
        body: {
          northId,
          zip: northData.zip,
        },
      });

      if (error) {
        logger.error('Error generating neighborhood data:', error);
        
        // Check if it's a rate limit error
        if (error.message?.includes('429') || error.message?.includes('Rate limit')) {
          toast({
            title: "Rate limit reached",
            description: "Please wait a moment before refreshing again",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: "Failed to fetch neighborhood data",
            variant: "destructive",
          });
        }
        return;
      }

      if (result?.data) {
        setData(result.data);
        
        // Reload data from database to ensure consistency (function saves to DB before returning)
        await loadExistingData();
        
        // Small delay to ensure database write is fully propagated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Notify parent component to refetch north data (for preview update)
        if (onUpdate) {
          await onUpdate();
        }
        
        toast({
          title: "Success",
          description: "Neighborhood data updated",
        });
      }
    } catch (error) {
      logger.error('Error generating neighborhood data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'Rising':
        return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'Easing':
        return <TrendingDown className="h-5 w-5 text-blue-600" />;
      default:
        return <Minus className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'Rising':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Easing':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const handleToggleVisibility = async (checked: boolean) => {
    try {
      const { error } = await supabase
        .from('norths')
        .update({ show_neighborhood_on_report: checked })
        .eq('id', northId);

      if (error) throw error;

      setShowOnReport(checked);
      
      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Notify parent component to refetch north data (for preview update)
      if (onUpdate) {
        await onUpdate();
      }
      
      toast({
        title: "Success",
        description: checked 
          ? "Neighborhood section will show on report" 
          : "Neighborhood section hidden from report",
      });
    } catch (error) {
      logger.error('Error updating visibility:', error);
      toast({
        title: "Error",
        description: "Failed to update visibility",
        variant: "destructive",
      });
    }
  };

  const getDemandColor = (rating: number) => {
    if (rating >= 7) return 'text-green-600';
    if (rating >= 5) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Generate summary mutation
  const generateSummary = useMutation({
    mutationFn: async () => {
      if (!data) throw new Error('No neighborhood data available');

      const { data: summaryData, error } = await supabase.functions.invoke('generate-neighborhood-summary', {
        body: {
          northData: {
            id: northId,
            address: northData.address,
            city: northData.city,
            state: northData.state,
            zip: northData.zip,
            price: northData.price,
            bedrooms: northData.bedrooms,
            bathrooms: northData.bathrooms,
            square_feet: northData.square_feet,
            neighborhood_data: data
          }
        }
      });

      if (error) throw error;
      if (!summaryData?.summary) throw new Error('No summary generated');

      return summaryData.summary;
    },
    onSuccess: async (summary) => {
      setNeighborhoodSummary(summary);

      const { error } = await supabase
        .from('norths')
        .update({ neighborhood_summary: summary })
        .eq('id', northId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['ai-summaries', northId] });

      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));

      // Notify parent component to refetch north data (for preview update)
      if (onUpdate) {
        await onUpdate();
      }

      toast({
        title: "Success",
        description: "Neighborhood summary has been generated successfully",
      });
    },
    onError: (error: any) => {
      logger.error('Error generating summary:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate summary",
        variant: "destructive",
      });
    },
  });

  const saveSummary = useMutation({
    mutationFn: async (summary: string) => {
      const { error } = await supabase
        .from('norths')
        .update({ neighborhood_summary: summary })
        .eq('id', northId);

      if (error) throw error;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['ai-summaries', northId] });
      queryClient.invalidateQueries({ queryKey: ['north-showings-summary', northId] });
      queryClient.invalidateQueries({ queryKey: ['comps', northId] });

      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));

      // Notify parent component to refetch north data (for preview update)
      if (onUpdate) {
        await onUpdate();
      }

      toast({
        title: "Success",
        description: "Neighborhood summary has been saved successfully",
      });
    },
    onError: (error: any) => {
      logger.error('Error saving summary:', error);
      toast({
        title: "Error",
        description: "Failed to save summary",
        variant: "destructive",
      });
    },
  });

  const handleGenerateSummary = () => {
    setIsGeneratingSummary(true);
    generateSummary.mutate(undefined, {
      onSettled: () => setIsGeneratingSummary(false)
    });
  };

  const handleSaveSummary = () => {
    saveSummary.mutate(neighborhoodSummary);
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {isPublished && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This report is published. Click "Unpublish" to make changes.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground">Neighborhood Intelligence</h2>
          <p className="text-xs md:text-sm text-muted-foreground mt-1 break-words">
            Real-time market analysis for {northData.city}, {northData.state} {northData.zip}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-on-report"
              checked={showOnReport}
              onCheckedChange={handleToggleVisibility}
              disabled={isPublished}
              style={showOnReport ? { backgroundColor: '#22c55e' } : undefined}
            />
            <Label htmlFor="show-on-report" className="text-sm font-medium whitespace-nowrap">
              Show on Report
            </Label>
          </div>
          <Button
            onClick={handleGenerateData}
            disabled={isLoading || isPublished}
            size="sm"
            className="w-full sm:w-auto"
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            <RefreshCw className={`h-4 w-4 md:mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{isLoading ? 'Analyzing...' : data ? 'Refresh Data' : 'Generate Analysis'}</span>
            <span className="sm:hidden">{isLoading ? 'Analyzing...' : data ? 'Refresh' : 'Generate'}</span>
          </Button>
        </div>
      </div>

      {data && (
        <div className="text-sm text-muted-foreground">
          Last updated: {formatDistanceToNow(new Date(data.updated_at), { addSuffix: true })}
        </div>
      )}

      {!data && !isLoading && (
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground mb-4">No neighborhood data available yet.</p>
            <p className="text-sm text-muted-foreground">
              Click "Generate Analysis" to fetch current market intelligence for this area.
            </p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            <Card>
              <CardHeader className="pb-2 md:pb-3">
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <Home className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                  Demand Rating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl md:text-4xl font-bold ${getDemandColor(data.analysis.demandRating)}`}>
                  {data.analysis.demandRating}/10
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mt-2">
                  {data.analysis.demandRating >= 7
                    ? 'Strong buyer demand'
                    : data.analysis.demandRating >= 5
                    ? 'Moderate demand'
                    : 'Softer market conditions'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 md:pb-3">
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  {getTrendIcon(data.analysis.buyerTrend)}
                  Competition Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Badge className={`text-sm md:text-lg px-2 md:px-3 py-1 ${getTrendColor(data.analysis.buyerTrend)}`}>
                  {data.analysis.buyerTrend}
                </Badge>
                <p className="text-xs md:text-sm text-muted-foreground mt-3">
                  {data.analysis.buyerTrend === 'Rising'
                    ? 'Increasing buyer competition'
                    : data.analysis.buyerTrend === 'Easing'
                    ? 'Competition is decreasing'
                    : 'Market competition is balanced'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 md:pb-3">
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <Clock className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                  Market Velocity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl md:text-4xl font-bold text-foreground">
                  {data.metrics.soldDomMedian || 'N/A'}
                  {data.metrics.soldDomMedian && <span className="text-base md:text-lg text-muted-foreground ml-1">days</span>}
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mt-2">
                  Median days to sell
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Metrics */}
          <Card>
            <CardHeader className="pb-3 md:pb-6">
              <CardTitle className="text-base md:text-lg">Market Activity</CardTitle>
              <CardDescription className="text-xs md:text-sm">Last 90 days of market data</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                <div>
                  <div className="text-xs md:text-sm font-medium text-muted-foreground mb-1">New Listings</div>
                  <div className="text-xl md:text-2xl font-bold text-foreground">{data.metrics.newListings}</div>
                  <div className="text-[10px] md:text-xs text-muted-foreground">Last 7 days</div>
                </div>

                <div>
                  <div className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Active Listings</div>
                  <div className="text-xl md:text-2xl font-bold text-foreground">{data.metrics.activeListings}</div>
                  <div className="text-[10px] md:text-xs text-muted-foreground">Currently for sale</div>
                </div>

                <div>
                  <div className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Pending Sales</div>
                  <div className="text-xl md:text-2xl font-bold text-orange-600">{data.metrics.pendingListings}</div>
                  <div className="text-[10px] md:text-xs text-muted-foreground">Under contract</div>
                </div>

                <div>
                  <div className="text-xs md:text-sm font-medium text-muted-foreground mb-1">Sold</div>
                  <div className="text-xl md:text-2xl font-bold text-green-600">{data.metrics.soldListings}</div>
                  <div className="text-[10px] md:text-xs text-muted-foreground">Last 90 days</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Price & Inventory Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <Tag className="h-4 w-4 md:h-5 md:w-5" />
                  Price Reductions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl md:text-3xl font-bold text-foreground">{data.metrics.priceReductions}</span>
                    <span className="text-base md:text-lg text-muted-foreground">listings</span>
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground">
                    {data.metrics.priceReductionRate}% of active listings have reduced prices
                  </div>
                  {data.metrics.priceReductionRate > 30 && (
                    <Badge variant="destructive" className="mt-2 text-xs">High reduction rate</Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base md:text-lg">Inventory Supply</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl md:text-3xl font-bold text-foreground">
                      {data.analysis.monthsOfInventory?.toFixed(1) || 'N/A'}
                    </span>
                    <span className="text-base md:text-lg text-muted-foreground">months</span>
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground">
                    {data.analysis.monthsOfInventory && data.analysis.monthsOfInventory < 3
                      ? "Seller's market - Low inventory"
                      : data.analysis.monthsOfInventory && data.analysis.monthsOfInventory > 6
                      ? "Buyer's market - High inventory"
                      : 'Balanced market'}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Days on Market Comparison */}
          {data.metrics.activeDomMedian && data.metrics.soldDomMedian && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base md:text-lg">Days on Market Comparison</CardTitle>
                <CardDescription className="text-xs md:text-sm">Active listings vs. recent sales</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 md:gap-8">
                  <div>
                    <div className="text-xs md:text-sm font-medium text-muted-foreground mb-2">Active Listings</div>
                    <div className="text-2xl md:text-4xl font-bold text-foreground">{data.metrics.activeDomMedian}</div>
                    <div className="text-xs md:text-sm text-muted-foreground mt-1">days (median)</div>
                  </div>
                  <div>
                    <div className="text-xs md:text-sm font-medium text-muted-foreground mb-2">Recent Sales</div>
                    <div className="text-2xl md:text-4xl font-bold text-green-600">{data.metrics.soldDomMedian}</div>
                    <div className="text-xs md:text-sm text-muted-foreground mt-1">days (median)</div>
                  </div>
                </div>
                {data.metrics.activeDomMedian > data.metrics.soldDomMedian * 1.4 && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs md:text-sm text-yellow-800">
                      ⚠️ Active listings are sitting longer than recent sales, indicating a potential market slowdown
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Summary Section */}
          <div className="mt-8 border-t pt-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-1">AI Summary</h3>
              <p className="text-sm text-muted-foreground">
                Generate an AI-powered summary of your neighborhood market intelligence
              </p>
            </div>

            <RichTextEditor
              value={neighborhoodSummary}
              onChange={setNeighborhoodSummary}
              onSave={(summary) => saveSummary.mutate(summary)}
              placeholder="Click 'Generate Summary' to create an AI summary of neighborhood data..."
              disabled={isPublished}
              className="mb-4"
            />

            <div className="flex gap-2">
              <Button
                onClick={handleGenerateSummary}
                disabled={isGeneratingSummary || isPublished || !data}
                style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
              >
                <Sparkles className={`h-4 w-4 mr-2 ${isGeneratingSummary ? 'animate-spin' : ''}`} />
                {isGeneratingSummary ? 'Generating...' : 'Generate Summary'}
              </Button>
              <Button
                onClick={handleSaveSummary}
                variant="outline"
                disabled={!neighborhoodSummary || isPublished || saveSummary.isPending}
                style={{ borderColor: secondaryColor || '#1b489b', color: secondaryColor || '#1b489b' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = secondaryColor || '#1b489b';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = secondaryColor || '#1b489b';
                }}
              >
                {saveSummary.isPending ? 'Saving...' : 'Save Summary'}
              </Button>
            </div>

            {neighborhoodSummary && (
              <p className="text-xs text-muted-foreground mt-2">
                This summary also appears in the AI Summaries menu on the report.
              </p>
            )}

            {!data && (
              <p className="text-sm text-muted-foreground mt-2">
                Generate neighborhood data first before creating a summary
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
